import * as Sentry from '@sentry/vue'
import { flattenCaughtMessage } from './errorUtils'
import { markImpendingReload } from './reloadLoopMonitor'
import { notifyGlobalToast, RECOVERY_SESSION_KEYS, tryOneTimeSessionReload } from './recoveryUi'
import { tryConsumeAutoReload } from './autoReloadBudget'

type ChunkReloadState = {
  count: number
  version: string
  lastReloadTime: number
}

type VersionResponse = {
  version?: string
  buildTime?: string
}

export type VersionCheckResult = {
  checked: boolean
  isNewVersionAvailable: boolean
  serverVersion: string | null
}

const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'unable to preload css',
  'loading css chunk',
  'load failed',
  'importing a module script failed',
  // HTML returned instead of JS (e.g. missing chunk → SPA index.html fallback)
  'not a valid javascript mime type',
  'failed to load module script',
  'expected a javascript module',
  // Shorter / alternate phrasing (bundlers, WebViews, minifiers)
  'dynamically imported module',
  'loading chunk',
  'chunkloaderror',
  // Vue Router: lazy `import()` resolved without a usable `default` (often same as chunk/CDN failure).
  "couldn't resolve component",
]

/**
 * Extra matchers for wording that varies by engine (Firefox, Edge, embedded WebViews).
 * Kept separate from substring list to avoid overly broad single-word matches.
 */
const CHUNK_LIKE_MESSAGE_REGEX =
  /loading\s+chunk\s+\d+\s+failed|chunk\s+load(?:\s+|\s+operation\s+)failed|import\s+failed|failed\s+to\s+import\s+module|error\s+when\s+loading\s+module/i

const VERSION_PATH = '/version.json'
const RELOAD_STATE_KEY = 'chunk-error-reload-state'
const MAX_RELOADS_PER_VERSION = 1
const MIN_RELOAD_INTERVAL_MS = 60_000

let activeChunkHandling: Promise<void> | null = null

const readReloadState = (): ChunkReloadState => {
  try {
    const raw = sessionStorage.getItem(RELOAD_STATE_KEY)
    if (!raw) {
      return { count: 0, version: '', lastReloadTime: 0 }
    }

    const parsed = JSON.parse(raw) as Partial<ChunkReloadState>
    return {
      count: Number(parsed.count ?? 0),
      version: String(parsed.version ?? ''),
      lastReloadTime: Number(parsed.lastReloadTime ?? 0),
    }
  } catch {
    return { count: 0, version: '', lastReloadTime: 0 }
  }
}

const writeReloadState = (state: ChunkReloadState): void => {
  try {
    sessionStorage.setItem(RELOAD_STATE_KEY, JSON.stringify(state))
  } catch {
    // Ignore storage failures; loop prevention still works via process memory.
  }
}

const shouldReloadForVersion = (serverVersion: string): boolean => {
  const now = Date.now()
  const state = readReloadState()

  if (state.version !== serverVersion) {
    return true
  }

  if (state.count >= MAX_RELOADS_PER_VERSION) {
    return false
  }

  if (now - state.lastReloadTime < MIN_RELOAD_INTERVAL_MS) {
    return false
  }

  return true
}

const markVersionReload = (serverVersion: string): void => {
  const now = Date.now()
  const state = readReloadState()
  const nextCount = state.version === serverVersion ? state.count + 1 : 1

  writeReloadState({
    version: serverVersion,
    count: nextCount,
    lastReloadTime: now,
  })
}

const notifyVersionRefreshNeeded = (): void => {
  notifyGlobalToast('warning', 'A new version is available. Please refresh this page.')
}

/**
 * Chunk failed but `version.json` matches the running build — likely transient (CDN, cache, offline).
 * Ask the user to refresh; auto-reload may have already run once this session.
 */
const notifySameVersionChunkFailure = (): void => {
  notifyGlobalToast(
    'warning',
    "We couldn't load part of the app. Please refresh the page. If this keeps happening, check your connection.",
  )
}

const reportRealChunkIssue = (
  error: unknown,
  details: { versionCheckFailed: boolean; serverVersion: string | null },
): void => {
  Sentry.captureException(error, {
    tags: {
      chunk_error_handled: 'real-issue',
      chunk_error_version_check: details.versionCheckFailed ? 'failed' : 'matched',
      chunk_error_server_version: details.serverVersion ?? 'unknown',
    },
    extra: {
      appVersion: getAppVersion(),
      serverVersion: details.serverVersion,
      versionCheckFailed: details.versionCheckFailed,
    },
  })
}

const getAppVersion = (): string => {
  return __APP_VERSION__
}

export const isChunkLoadError = (error: unknown): boolean => {
  const message = flattenCaughtMessage(error).toLowerCase()
  if (CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern))) {
    return true
  }
  return CHUNK_LIKE_MESSAGE_REGEX.test(message)
}

export const checkVersionMismatch = async (): Promise<VersionCheckResult> => {
  try {
    const response = await fetch(`${VERSION_PATH}?_t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
      },
    })

    if (!response.ok) {
      return { checked: false, isNewVersionAvailable: false, serverVersion: null }
    }

    const payload = (await response.json()) as VersionResponse

    // `version` is the build-time git SHA baked into the bundle; hosting stamp preserves it.
    const serverVersion = typeof payload.version === 'string' ? payload.version.trim() : undefined

    if (!serverVersion) {
      return { checked: false, isNewVersionAvailable: false, serverVersion: null }
    }

    return {
      checked: true,
      serverVersion,
      isNewVersionAvailable: serverVersion !== getAppVersion(),
    }
  } catch {
    return { checked: false, isNewVersionAvailable: false, serverVersion: null }
  }
}

let activeVersionMismatchHandling: Promise<boolean> | null = null

/** Reasons that wait for a new service worker instead of reloading under the current one. */
const SW_DEFERRED_RELOAD_REASONS = new Set(['version_poll', 'version_mismatch_chunk'])

/** Polls that may wait on one server version's worker install before falling back to a reload. */
const MAX_SW_UPDATE_DEFERRALS_PER_VERSION = 3

const swUpdateDeferralsByVersion = new Map<string, number>()

/** Test helper — clears SW update deferral counts between cases. */
export function resetSwUpdateDeferralsForTests(): void {
  swUpdateDeferralsByVersion.clear()
}

/**
 * While a worker controls this tab, a reload is answered from its precache (old bundle).
 * Ask for the new `sw.js` and report whether a worker is now installing or waiting; its
 * `controllerchange` reload (registerSW.ts) then loads the new precache.
 */
const deferReloadToServiceWorkerUpdate = async (serverVersion: string): Promise<boolean> => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return false
  }

  const controllerBefore = navigator.serviceWorker.controller
  if (!controllerBefore) {
    return false
  }

  const deferrals = swUpdateDeferralsByVersion.get(serverVersion) ?? 0
  if (deferrals >= MAX_SW_UPDATE_DEFERRALS_PER_VERSION) {
    return false
  }

  try {
    const reg = await navigator.serviceWorker.getRegistration()
    if (!reg) {
      return false
    }

    await reg.update()

    // controllerchange already fired while we awaited; it was folded into this call, so reload here.
    if (navigator.serviceWorker.controller !== controllerBefore) {
      return false
    }

    if (!reg.installing && !reg.waiting) {
      return false
    }
  } catch {
    return false
  }

  swUpdateDeferralsByVersion.set(serverVersion, deferrals + 1)
  return true
}

/**
 * Reload this tab when the deployed build is newer than the running bundle.
 *
 * Compares GET /version.json `version` to __APP_VERSION__ (build-time git SHA). On mismatch: one automatic reload per
 * server version per session (see RELOAD_STATE_KEY / shouldReloadForVersion); otherwise a
 * toast asking the user to refresh.
 *
 * Callers:
 * - registerSW.ts — `controllerchange` → reloadReason `sw_controllerchange`
 *   (reloads once the new SW controls the tab, so the reload is served by the new precache).
 * - registerSW.ts — visible-tab poll every 90s → reloadReason `version_poll`
 * - handleChunkLoadError — reloadReason `version_mismatch_chunk`
 *   (reactive; runs only after a lazy chunk / dynamic import fails).
 *
 * `version_poll` and `version_mismatch_chunk` first call `registration.update()` when a SW
 * controls the tab and skip the reload while a new worker installs (see
 * deferReloadToServiceWorkerUpdate).
 */
export async function tryReloadForServerVersionMismatch(reloadReason: string): Promise<boolean> {
  if (activeVersionMismatchHandling) {
    return activeVersionMismatchHandling
  }

  activeVersionMismatchHandling = (async () => {
    const versionCheck = await checkVersionMismatch()
    const hasExplicitMismatch =
      versionCheck.checked &&
      versionCheck.isNewVersionAvailable &&
      versionCheck.serverVersion !== null

    if (!hasExplicitMismatch) {
      return false
    }

    const serverVersion = versionCheck.serverVersion as string
    if (
      SW_DEFERRED_RELOAD_REASONS.has(reloadReason) &&
      (await deferReloadToServiceWorkerUpdate(serverVersion))
    ) {
      return false
    }

    if (shouldReloadForVersion(serverVersion)) {
      const budget = tryConsumeAutoReload(reloadReason)
      if (!budget.ok) {
        notifyVersionRefreshNeeded()
        return false
      }

      markVersionReload(serverVersion)
      markImpendingReload(`${reloadReason}:${serverVersion}`)
      window.location.reload()
      return true
    }

    notifyVersionRefreshNeeded()
    return false
  })()

  try {
    return await activeVersionMismatchHandling
  } finally {
    activeVersionMismatchHandling = null
  }
}

export const handleChunkLoadError = async (error: unknown): Promise<void> => {
  if (!isChunkLoadError(error)) return

  if (activeChunkHandling) {
    await activeChunkHandling
    return
  }

  activeChunkHandling = (async () => {
    const versionCheck = await checkVersionMismatch()
    const hasExplicitMismatch =
      versionCheck.checked &&
      versionCheck.isNewVersionAvailable &&
      versionCheck.serverVersion !== null

    // Chunk failure + newer server build — same reload helper as SW controllerchange (see above).
    if (hasExplicitMismatch) {
      await tryReloadForServerVersionMismatch('version_mismatch_chunk')
      return
    }

    reportRealChunkIssue(error, {
      versionCheckFailed: !versionCheck.checked,
      serverVersion: versionCheck.serverVersion,
    })

    if (tryOneTimeSessionReload(RECOVERY_SESSION_KEYS.chunkSameVersion)) {
      return
    }

    notifySameVersionChunkFailure()
  })()

  try {
    await activeChunkHandling
  } finally {
    activeChunkHandling = null
  }
}
